import { Node } from '@tiptap/core'
import { VueNodeViewRenderer } from '@tiptap/vue-2'
import NodeViewUserMention from './NodeViewUserMention.vue'

export const UserMention = Node.create({
  name: 'userMention',
  group: 'inline',
  inline: true,
  selectable: false,
  atom: true, // Makes it a single unit that can't be edited internally

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: element => element.getAttribute('data-id'),
        renderHTML: attributes => ({ 'data-id': attributes.id }),
      },
      label: {
        default: null,
        parseHTML: element => element.getAttribute('data-label'),
        renderHTML: attributes => ({ 'data-label': attributes.label }),
      },
      category: {
        default: null,
        parseHTML: element => element.getAttribute('data-category'),
        renderHTML: attributes => ({ 'data-category': attributes.category }),
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-type="user-mention"]',
      },
    ]
  },

  renderHTML({ node, HTMLAttributes }) {
    return ['span', {
      'data-type': 'user-mention',
      'class': 'editor-user-mention',
      ...HTMLAttributes,
    }, node.attrs.label]
  },

  addNodeView() {
    return VueNodeViewRenderer(NodeViewUserMention)
  },

  addCommands() {
    return {
      insertUserMention: attributes => ({ chain }) => {
        // Ensure attributes include id and label at minimum
        if (!attributes || !attributes.id || !attributes.label) {
          console.error('Cannot insert user mention without id and label attributes.')
          return false
        }
        return chain().insertContent({
          type: this.name, // Use the node's name ('userMention')
          attrs: attributes,
        }).run()
      },
      removeUserMention: mentionId => ({ tr, state, dispatch }) => {
        const { doc } = state
        let mentionFound = false
        let fromPos = -1
        let toPos = -1

        // Find the mention node by its ID
        doc.descendants((node, pos) => {
          if (node.type.name === this.name && node.attrs.id === mentionId) {
            fromPos = pos
            toPos = pos + node.nodeSize
            mentionFound = true
            return false // Stop searching once found
          }
          // Continue searching if not found yet
          return true
        })

        // If found, delete the node
        if (mentionFound && fromPos !== -1 && toPos !== -1 && dispatch) {
          tr.delete(fromPos, toPos)
          dispatch(tr)
          return true
        }

        return false
      },
    }
  },

  addStorage() {
    return {
      markdown: {
        // How to serialize this node to Markdown: [@Label](mention://category/id/Label)
        serialize: (state, node) => {
          const { id, label, category } = node.attrs
          // Ensure id, label, and category are present for this format
          if (!id || !label || !category) {
            console.warn('Skipping mention serialization: Missing id, label, or category.', node.attrs)
            // Optionally, write the label as plain text if needed:
            // state.write(label || '');
            return
          }

          // URL-encode components to handle special characters in the URL path
          const encodedCategory = encodeURIComponent(category)
          const encodedId = encodeURIComponent(id)
          const encodedLabel = encodeURIComponent(label) // Encode label for the URL part

          // Write the markdown link
          // Note: The label in [@Label] doesn't strictly need encoding for common markdown,
          // but be mindful of characters like ']' if they appear in labels.
          // We are NOT encoding the label within the square brackets here.
          state.write(`[@${label}](${state.options.linkPrefix || ''}mention://${encodedCategory}/${encodedId}/${encodedLabel})`)
        },
        // How to parse this node from Markdown
        parse: {
          // This is run when markdown-it is initialized
          setup(markdownit) {
            // Renderer rule for the opening tag (remains the same)
            // Creates the <span data-type="user-mention" ...> tag
            markdownit.renderer.rules.user_mention_open = (tokens, idx) => {
              const token = tokens[idx]
              const id = token.attrGet('id')
              const label = token.attrGet('label')
              const category = token.attrGet('category')

              let attrs = `data-type="user-mention" data-id="${id}" data-label="${label}"`
              if (category) {
                attrs += ` data-category="${category}"`
              }
              // Add the class for styling if needed
              attrs += ` class="editor-user-mention"`
              return `<span ${attrs}>`
            }

            // Renderer rule for the closing tag (remains the same)
            markdownit.renderer.rules.user_mention_close = () => {
              return '</span>'
            }

            // Inline ruler rule to detect the mention link syntax [@Label](mention://...)
            markdownit.inline.ruler.before('link', 'user_mention_link', (state, silent) => {
              const max = state.posMax
              const startPos = state.pos // Keep track of the initial position

              // Check for starting sequence: [@
              if (state.src.charCodeAt(startPos) !== 0x5B /* [ */ || state.src.charCodeAt(startPos + 1) !== 0x40 /* @ */) {
                return false
              }

              const labelStart = startPos + 2 // Start after [@
              // parseLinkLabel handles spaces within the label correctly
              const labelEnd = state.md.helpers.parseLinkLabel(state, startPos + 1, false)

              // Didn't find ]
              if (labelEnd < 0) {
                return false
              }

              let pos = labelEnd + 1 // Position after ]
              const labelText = state.src.slice(labelStart, labelEnd)

              // Next character must be (
              if (pos < max && state.src.charCodeAt(pos) === 0x28 /* ( */) {
                pos++ // Move past (

                // --- Updated Destination Parsing ---
                const destStart = pos
                let destEnd = -1
                let parenNestingLevel = 1 // Start at 1 because we are already inside the first '('
                let escaped = false

                while (pos < max) {
                  const code = state.src.charCodeAt(pos)

                  if (escaped) {
                    // Skip the escaped character
                    escaped = false
                  }
                  else if (code === 0x5C /* \ */) {
                    // Mark the next character as escaped
                    escaped = true
                  }
                  else if (code === 0x28 /* ( */) {
                    // Increase nesting level for opening parenthesis
                    parenNestingLevel++
                  }
                  else if (code === 0x29 /* ) */) {
                    // Decrease nesting level for closing parenthesis
                    parenNestingLevel--
                    if (parenNestingLevel === 0) {
                      // Found the matching closing parenthesis
                      destEnd = pos
                      break
                    }
                  }
                  // Move to the next character. Spaces are implicitly handled here
                  // as they don't match any special conditions (\, (, )).
                  pos++
                }
                // --- End of Updated Destination Parsing ---

                // Ensure we found the matching closing parenthesis
                if (destEnd !== -1) {
                  // Extract the destination content (including spaces)
                  const destination = state.src.slice(destStart, destEnd)
                  // Use regex to parse the custom mention:// URL
                  // The (.+) part will now correctly capture labels with spaces
                  const mentionMatch = destination.match(/^mention:\/\/([^/]+)\/([^/]+)\/(.+)$/)

                  if (mentionMatch) {
                    const encodedCategory = mentionMatch[1]
                    const encodedId = mentionMatch[2]
                    const encodedLabel = mentionMatch[3] // This might contain spaces

                    // Decode the components
                    const category = decodeURIComponent(encodedCategory)
                    const id = decodeURIComponent(encodedId)
                    const urlLabel = decodeURIComponent(encodedLabel) // Decode label which might have spaces

                    // Validate: label in brackets should match label in URL
                    if (labelText !== urlLabel) {
                      console.warn('Mention label mismatch:', `"${labelText}"`, '!==', `"${urlLabel}"`)
                      // Continue using labelText as the primary label
                    }

                    if (silent) {
                      // In silent mode, we only care if it matches, advance position and return true
                      state.pos = destEnd + 1
                      return true
                    }

                    // --- Token Pushing (remains the same) ---
                    const tokenOpen = state.push('user_mention_open', 'span', 1)
                    tokenOpen.attrs = [
                      ['id', id],
                      ['label', labelText], // Use the label from brackets
                      ['category', category],
                    ]

                    const tokenText = state.push('text', '', 0)
                    tokenText.content = `@${labelText}` // Display as @Label

                    state.push('user_mention_close', 'span', -1)
                    // --- End of Token Pushing ---

                    // Advance the parser position past the entire matched link (including the final ')')
                    state.pos = destEnd + 1
                    return true // Match successful
                  }
                }
              }
              // If parsing failed at any point, reset position and return false
              state.pos = startPos // Important: reset if match failed partway
              return false
            })
          },
        },
      },
    }
  },
})

// To insert
// editor.commands.insertUserMention({ id: 'user-123', label: 'John Doe', category: 'team' });

// To remove
// editor.commands.removeUserMention('user-123');
